import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const DEFAULT_INACTIVITY_MINUTES = 15;
const DEFAULT_LOGOUT_AFTER_LOCK_MINUTES = 5;
const WARNING_BEFORE_MS = 60 * 1000;
const TICK_INTERVAL_MS = 5000;

// Persistance du verrouillage : survit à un rafraîchissement de page pour empêcher
// de contourner l'écran de verrouillage en actualisant simplement l'onglet.
export const LOCK_STORAGE_KEY = 'dosnet_session_locked_at';
export const AUTO_LOGOUT_REASON_KEY = 'dosnet_auto_logout_reason';

interface UseInactivityLockReturn {
  isLocked: boolean;
  showWarning: boolean;
  secondsRemaining: number;
  inactivityMinutes: number;
  logoutAfterLockMinutes: number;
  unlock: () => void;
  resetTimer: () => void;
}

export function useInactivityLock(enabled: boolean = true): UseInactivityLockReturn {
  const [isLocked, setIsLocked] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  const [inactivityMinutes, setInactivityMinutes] = useState(DEFAULT_INACTIVITY_MINUTES);
  const [logoutAfterLockMinutes, setLogoutAfterLockMinutes] = useState(DEFAULT_LOGOUT_AFTER_LOCK_MINUTES);

  const inactivityMsRef = useRef(DEFAULT_INACTIVITY_MINUTES * 60 * 1000);
  const logoutAfterLockMsRef = useRef(DEFAULT_LOGOUT_AFTER_LOCK_MINUTES * 60 * 1000);
  const inactivityMinutesRef = useRef(DEFAULT_INACTIVITY_MINUTES);
  const logoutAfterLockMinutesRef = useRef(DEFAULT_LOGOUT_AFTER_LOCK_MINUTES);

  const lastActivityRef = useRef<number>(Date.now());
  const lockedAtRef = useRef<number | null>(null);

  const isLockedRef = useRef(false);
  const enabledRef = useRef(enabled);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const configLoadedRef = useRef(false);

  // Flag pour ignorer les événements auth pendant le déverrouillage
  const isUnlockingRef = useRef(false);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || configLoadedRef.current) return;
    configLoadedRef.current = true;

    const fetchDurations = async () => {
      try {
        const { data } = await supabase
          .from('parametres_systeme')
          .select('cle, valeur')
          .in('cle', ['duree_inactivite_minutes', 'duree_avant_deconnexion_minutes']);

        if (data) {
          const inactParam = data.find(p => p.cle === 'duree_inactivite_minutes');
          const logoutParam = data.find(p => p.cle === 'duree_avant_deconnexion_minutes');

          if (inactParam?.valeur) {
            const minutes = Math.max(1, parseInt(inactParam.valeur, 10) || DEFAULT_INACTIVITY_MINUTES);
            setInactivityMinutes(minutes);
            inactivityMsRef.current = minutes * 60 * 1000;
            inactivityMinutesRef.current = minutes;
          }

          if (logoutParam?.valeur) {
            const minutes = Math.max(1, parseInt(logoutParam.valeur, 10) || DEFAULT_LOGOUT_AFTER_LOCK_MINUTES);
            setLogoutAfterLockMinutes(minutes);
            logoutAfterLockMsRef.current = minutes * 60 * 1000;
            logoutAfterLockMinutesRef.current = minutes;
          }
        }
      } catch {
        // Utiliser les valeurs par défaut
      }
    };

    fetchDurations();
  }, [enabled]);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const startCountdown = useCallback((totalSeconds: number) => {
    stopCountdown();
    setSecondsRemaining(totalSeconds);
    countdownRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          stopCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [stopCountdown]);

  const handleAutoLogout = useCallback(async (raison: string = 'Inactivité prolongée après verrouillage') => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;

      if (userId) {
        await supabase.from('audit_logs').insert({
          user_id: userId,
          action: 'deconnexion_automatique',
          entite: 'session',
          details: {
            raison,
            duree_inactivite_minutes: inactivityMinutesRef.current,
            duree_verrouillage_minutes: logoutAfterLockMinutesRef.current,
          },
          user_agent: navigator.userAgent || null,
        });
      }
    } catch {
      // Ignorer les erreurs de log
    }

    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // Ignorer
    }

    // Nettoyer le marqueur de verrouillage et signaler la raison à l'écran de connexion
    localStorage.removeItem(LOCK_STORAGE_KEY);
    sessionStorage.setItem(AUTO_LOGOUT_REASON_KEY, 'inactivite');

    // Réinitialiser l'état de verrouillage : SessionSecurityProvider n'est jamais démonté
    // entre la déconnexion et la reconnexion (il est au-dessus du Router), donc sans ceci
    // `isLocked` restait bloqué à `true` et l'écran de verrouillage réapparaissait dès la
    // connexion suivante, même avec les bons identifiants.
    isLockedRef.current = false;
    lockedAtRef.current = null;
    lastActivityRef.current = Date.now();
    stopCountdown();
    setIsLocked(false);
    setShowWarning(false);
    setSecondsRemaining(60);

    window.dispatchEvent(new CustomEvent('force-logout'));
  }, [stopCountdown]);

  const startTick = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);

    tickRef.current = setInterval(() => {
      if (!enabledRef.current) return;

      const now = Date.now();

      if (isLockedRef.current) {
        if (lockedAtRef.current !== null) {
          const timeSinceLock = now - lockedAtRef.current;
          const remaining = Math.max(0, logoutAfterLockMsRef.current - timeSinceLock);

          if (remaining <= 0) {
            if (tickRef.current) clearInterval(tickRef.current);
            stopCountdown();
            handleAutoLogout();
          } else {
            setSecondsRemaining(Math.ceil(remaining / 1000));
          }
        }
        return;
      }

      const timeSinceActivity = now - lastActivityRef.current;
      const inactivityMs = inactivityMsRef.current;
      const timeUntilLock = inactivityMs - timeSinceActivity;

      if (timeUntilLock <= 0) {
        isLockedRef.current = true;
        lockedAtRef.current = now;
        localStorage.setItem(LOCK_STORAGE_KEY, String(now));
        setIsLocked(true);
        setShowWarning(false);
        stopCountdown();
        startCountdown(Math.ceil(logoutAfterLockMsRef.current / 1000));
      } else if (timeUntilLock <= WARNING_BEFORE_MS) {
        const secs = Math.ceil(timeUntilLock / 1000);
        setShowWarning(true);
        setSecondsRemaining(secs);
      } else {
        setShowWarning(false);
      }
    }, TICK_INTERVAL_MS);
  }, [handleAutoLogout, startCountdown, stopCountdown]);

  const resetTimer = useCallback(() => {
    if (!enabledRef.current || isLockedRef.current) return;
    lastActivityRef.current = Date.now();
    setShowWarning(false);
  }, []);

  const unlock = useCallback(() => {
    // Marquer qu'on est en train de déverrouiller pour ignorer les événements auth
    isUnlockingRef.current = true;

    isLockedRef.current = false;
    lockedAtRef.current = null;
    lastActivityRef.current = Date.now();
    localStorage.removeItem(LOCK_STORAGE_KEY);

    setIsLocked(false);
    setShowWarning(false);
    setSecondsRemaining(60);
    stopCountdown();

    // Relâcher le flag après un court délai (laisser les événements auth passer)
    setTimeout(() => {
      isUnlockingRef.current = false;
    }, 2000);
  }, [stopCountdown]);

  useEffect(() => {
    if (!enabled) return;

    // La session était verrouillée avant que la page ne soit rechargée/rouverte
    // (l'utilisateur n'a jamais ressaisi son mot de passe) : on ne relance pas
    // un nouveau délai d'inactivité, on déconnecte immédiatement.
    if (localStorage.getItem(LOCK_STORAGE_KEY)) {
      handleAutoLogout('Rafraîchissement de la page pendant le verrouillage de session');
      return;
    }

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'] as const;

    const handleActivity = () => {
      if (!isLockedRef.current) {
        lastActivityRef.current = Date.now();
      }
    };

    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));
    lastActivityRef.current = Date.now();
    startTick();

    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (tickRef.current) clearInterval(tickRef.current);
      stopCountdown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    isLockedRef.current = isLocked;
    if (!isLocked) {
      lockedAtRef.current = null;
    }
  }, [isLocked]);

  return {
    isLocked,
    showWarning,
    secondsRemaining,
    inactivityMinutes,
    logoutAfterLockMinutes,
    unlock,
    resetTimer,
  };
}
